import { AdminOnlyPermission, PermissionModules } from './permission.enum';

export type PermissionLevel = 'none' | 'view' | 'manage';

export type PermissionMap = Record<string, PermissionLevel>;

export interface ModulePermission {
  module: string;
  level: Exclude<PermissionLevel, 'none'>;
}

export const PERMISSION_LEVEL_RANK: Record<PermissionLevel, number> = {
  none: 0,
  view: 1,
  manage: 2
};

const view = (module: string): ModulePermission => ({ module, level: 'view' });
const manage = (module: string): ModulePermission => ({ module, level: 'manage' });

export const PERMISSION_KEY_MAP: Record<string, ModulePermission> = {
  [PermissionModules.VIEW_CLIENT]: view('client'),
  [PermissionModules.MANAGE_CLIENT]: manage('client'),
  [PermissionModules.VIEW_PROJECT]: view('project'),
  [PermissionModules.MANAGE_PROJECT]: manage('project'),
  [PermissionModules.MANAGE_PROJECT_STATUS]: manage('project_status'),
  [PermissionModules.VIEW_FIXED_BID_LOCK]: view('fixed_bid_lock'),
  [PermissionModules.MANAGE_FIXED_BID_LOCK]: manage('fixed_bid_lock'),

  [PermissionModules.VIEW_PL]: view('pnl'),
  [PermissionModules.MANAGE_PL]: manage('pnl'),
  [PermissionModules.VIEW_COMPARE_PL]: view('pnl_comparison'),
  [PermissionModules.MANAGE_COMPARE_PL]: manage('pnl_comparison'),
  [PermissionModules.VIEW_PL_PLUGS]: view('pnl_plugs'),
  [PermissionModules.MANAGE_PL_PLUGS]: manage('pnl_plugs'),
  [PermissionModules.VIEW_NET_MARGIN]: view('net_margin'),

  [PermissionModules.VIEW_BENCH_REPORT]: view('bench_report'),
  [PermissionModules.MANAGE_BENCH_REPORT]: manage('bench_report'),
  [PermissionModules.VIEW_STAFFED_POSITION_REPORT]: view('positions_report'),
  [PermissionModules.MANAGE_STAFFED_POSITION_REPORT]: manage('positions_report'),
  [PermissionModules.VIEW_BILL_RATE]: view('financial_data'),
  [PermissionModules.VIEW_OPEN_POSITION_REPORT]: view('positions_report'),
  [PermissionModules.MANAGE_OPEN_POSITION_REPORT]: manage('positions_report'),
  [PermissionModules.VIEW_BILL_RATE_OPEN_POS]: view('financial_data'),

  [PermissionModules.VIEW_EMPLOYEE]: view('employee'),
  [PermissionModules.MANAGE_EMPLOYEE]: manage('employee'),
  [PermissionModules.VIEW_WORK_EXCEPTION]: view('work_exceptions'),
  [PermissionModules.MANAGE_WORK_EXCEPTION]: manage('work_exceptions'),
  [PermissionModules.VIEW_IMPORT_ACTUALS]: view('import_actuals'),
  [PermissionModules.MANAGE_IMPORT_ACTUALS]: manage('import_actuals'),
  [PermissionModules.VIEW_TIME_ENTRIES]: view('time_entries'),
  [PermissionModules.MANAGE_TIME_ENTRIES]: manage('time_entries'),
  [PermissionModules.View_Create_Entries]: view('create_entries'),
  [PermissionModules.Create_TIME_ENTRIES]: manage('create_entries'),
  [PermissionModules.View_Work_Exception_Request]: view('work_exception_request'),
  [PermissionModules.Manage_Work_Exception_Request]: manage('work_exception_request'),

  [PermissionModules.VIEW_EXPENSE_TYPE]: view('expense_type'),
  [PermissionModules.MANAGE_EXPENSE_TYPE]: manage('expense_type'),
  [PermissionModules.VIEW_MONTHLY_EXPENSE_TYPE]: view('monthly_expense_type'),
  [PermissionModules.MANAGE_MONTHLY_EXPENSE_TYPE]: manage('monthly_expense_type'),
  [PermissionModules.VIEW_EMPLOYEE_TYPE]: view('employee_type'),
  [PermissionModules.MANAGE_EMPLOYEE_TYPE]: manage('employee_type'),
  [PermissionModules.VIEW_POSITION_TYPE]: view('position_type'),
  [PermissionModules.MANAGE_POSITION_TYPE]: manage('position_type'),
  [PermissionModules.VIEW_HOLIDAY]: view('holidays'),
  [PermissionModules.MANAGE_HOLIDAY]: manage('holidays'),
  [PermissionModules.VIEW_WORK_EXCEPTION_TYPE]: view('work_exception_type'),
  [PermissionModules.MANAGE_WORK_EXCEPTION_TYPE]: manage('work_exception_type'),
  [PermissionModules.VIEW_MANAGE_TAG_CATEGORY]: view('tag_category'),
  [PermissionModules.CREATE_TAG_CATEGORY]: manage('tag_category'),
  [PermissionModules.MANAGE_TAG_CATEGORY]: manage('tag_category'),
  [PermissionModules.VIEW_MANAGE_TAG_SUB_CATEGORY]: view('tag_category'),
  [PermissionModules.CREATE_TAG_SUB_CATEGORY]: manage('tag_category'),
  [PermissionModules.MANAGE_TAG_SUB_CATEGORY]: manage('tag_category'),
  [PermissionModules.VIEW_MANAGE_TAG]: view('tag_category'),
  [PermissionModules.CREATE_TAG]: manage('tag_category'),
  [PermissionModules.MANAGE_TAG]: manage('tag_category'),
  [PermissionModules.View_Extended_Filed]: view('global_details'),
  [PermissionModules.MANAGE_Extended_Filed]: manage('global_details'),
  [AdminOnlyPermission.MANAGE_GLOBAL_DETAIL]: manage('global_details'),

  [PermissionModules.VIEW_MANAGE_USER]: view('manage_user'),
  [PermissionModules.CREATE_USER]: manage('manage_user'),
  [PermissionModules.MANAGE_USER]: manage('manage_user'),
  [PermissionModules.VIEW_ROLE]: view('role'),
  [PermissionModules.MANAGE_ROLE]: manage('role'),

  [PermissionModules.VIEW_SHARE_FILTER]: view('share_filter'),
  [PermissionModules.MANAGE_SHARE_FILTER]: manage('share_filter')
};

export function resolvePermissionKey(key: string): ModulePermission | undefined {
  return PERMISSION_KEY_MAP[key];
}

export function hasPermissionLevel(granted: PermissionMap | null | undefined, required: ModulePermission | undefined): boolean {
  if (!required) {
    return false;
  }
  const grantedLevel: PermissionLevel = (granted && granted[required.module]) || 'none';
  const grantedRank = PERMISSION_LEVEL_RANK[grantedLevel] ?? 0;
  return grantedRank >= PERMISSION_LEVEL_RANK[required.level];
}

export function isKeyPermitted(granted: PermissionMap | null | undefined, key: string): boolean {
  return hasPermissionLevel(granted, resolvePermissionKey(key));
}
